import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TableCell, TableRow } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { NumberField, TabButtonGroup } from 'src/Libs/inputs/react-hook-form'
import {
  PHASE_SHIFT,
  REACTIVE_POWER_FUNCTION_MODEL,
  ReactivePowerFixedFunctionWithValidations,
  ReactivePowerFunctionWithValidations,
} from '../../types/common'
import { ReactivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { getChangedFieldStyle } from '../../utils'
import { NAME_SPACE } from './constants'
import { getFirstFieldDisabledState, getFulcrumFieldHasChangedValues, getSecondFieldDisabledState } from './helpers'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
type Props = {
  index: number
  selectedFunction: REACTIVE_POWER_FUNCTION_MODEL
  fulcrum: [number | null, number | null, PHASE_SHIFT]
  fulcrums: ReactivePowerSettingsModel['fulcrums']
}

const ReactivePowerSettingsTableRow: React.FC<Props> = ({ index, selectedFunction, fulcrum, fulcrums }) => {
  const { setValue } = useFormContext()
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights

  const fulcrumItem = fulcrums?.find(f => selectedFunction in f)
  if (!fulcrumItem) return null

  const isFixedFunction = selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixedQ || selectedFunction === REACTIVE_POWER_FUNCTION_MODEL.fixCosPhi
  const fieldLimits: ReactivePowerFixedFunctionWithValidations['value'] | ReactivePowerFunctionWithValidations['limits'] =
    Object.values(fulcrumItem)[0][isFixedFunction ? 'value' : 'limits']

  const isFirstFieldDisabled = getFirstFieldDisabledState(isFixedFunction, Object.values(fulcrumItem)[0].limits, fulcrum[0], index)
  const isSecondFieldDisabled = getSecondFieldDisabledState(fieldLimits, fulcrum[1], index)
  const isThirdFieldDisabled = fulcrum[1] === null || fulcrum[1] === 0 || isSecondFieldDisabled

  const { firstFieldHasChangedValue, secondFieldHasChangedValue } = getFulcrumFieldHasChangedValues(selectedFunction, fulcrumItem, index)

  const phaseShiftOptions: { label: string; value: PHASE_SHIFT }[] = [
    {
      label: t(`${NAME_SPACE}.fulcrums.phase-shift.inductive`),
      value: PHASE_SHIFT.inductive,
    },
    {
      label: t(`${NAME_SPACE}.fulcrums.phase-shift.capacitive`),
      value: PHASE_SHIFT.capacitive,
    },
  ]

  return (
    <TableRow>
      <TableCell scope="row">{index + 1}</TableCell>
      <TableCell align="center">
        <NumberField
          name={`fulcrums.${selectedFunction}.${index}.0`}
          disabled={isFirstFieldDisabled || !canEdit}
          sx={getChangedFieldStyle(firstFieldHasChangedValue)}
          fullWidth
        />
      </TableCell>
      <TableCell align="center">
        <NumberField
          name={`fulcrums.${selectedFunction}.${index}.1`}
          disabled={isSecondFieldDisabled || !canEdit}
          sx={getChangedFieldStyle(secondFieldHasChangedValue)}
          fullWidth
          propsOnChange={value => {
            // ? Two things can't be true at the same time: second field being negative and third field being inductive or vice versa
            if (Number(value) < 0) {
              setValue(`fulcrums.${selectedFunction}.${index}.2`, PHASE_SHIFT.capacitive)
            } else {
              setValue(`fulcrums.${selectedFunction}.${index}.2`, PHASE_SHIFT.inductive)
            }
          }}
        />
      </TableCell>
      <TableCell align="center">
        <TabButtonGroup
          sx={{ minWidth: 'min-content', mt: 0 }}
          name={`fulcrums.${selectedFunction}.${index}.2`}
          defaultTabIndex={fulcrum[2]}
          options={phaseShiftOptions}
          disabled={isThirdFieldDisabled || !canEdit}
          onChange={value => {
            // ? Two things can't be true at the same time: second field being negative and third field being inductive or vice versa
            if (value === PHASE_SHIFT.capacitive && fulcrum[1]) {
              setValue(`fulcrums.${selectedFunction}.${index}.1`, fulcrum[1] * -1)
            }
            if (value === PHASE_SHIFT.inductive && fulcrum[1]) {
              setValue(`fulcrums.${selectedFunction}.${index}.1`, Math.abs(fulcrum[1]))
            }
          }}
        />
      </TableCell>
    </TableRow>
  )
}

export default ReactivePowerSettingsTableRow
